import React, { useState } from 'react';
import { Layout, Menu, Button, Typography, Space } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { menuConfig } from '../data/menuConfig';
import UserMenu from '../components/common/UserMenu';
import MultipleSequenceAlignment from '../components/results/MultipleSequenceAlignment';
import BatchFastaConverter from '../components/input/BatchFastaConverter';
import StructureAnalysisButton from '../components/structure-analysis/StructureAnalysisButton';

const { Header, Sider, Content, Footer } = Layout;
const { Title } = Typography;

interface SidebarLayoutProps {
  children: React.ReactNode;
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // 转换menuConfig为Menu组件需要的格式
  const menuItems = menuConfig.map(item => ({
    key: item.key,
    icon: item.icon,
    label: item.label,
    onClick: () => navigate(item.path)
  }));

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 左侧边栏 */}
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        width={250}
        style={{
          background: '#fff',
          boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          overflow: 'hidden'
        }}
      >
        <div style={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column' 
        }}>
          {/* 侧边栏头部 */}
          <div style={{ 
            padding: '16px', 
            textAlign: 'center',
            borderBottom: '1px solid #f0f0f0',
            flexShrink: 0
          }}>
            {collapsed ? (
              <img 
                src="/icon.png" 
                alt="Icon" 
                style={{ 
                  height: '32px',
                  width: '32px',
                  objectFit: 'contain'
                }} 
              />
            ) : (
              <img 
                src="/cusabio-logo.png" 
                alt="Cusabio Logo" 
                style={{ 
                  height: '40px',
                  width: 'auto',
                  maxWidth: '200px',
                  objectFit: 'contain'
                }} 
              />
            )}
          </div>
          
          {/* 导航菜单 */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            <Menu
              mode="inline"
              selectedKeys={[location.pathname]}
              items={menuItems}
              style={{ border: 'none' }}
            />
          </div>

          {/* 用户菜单 - 固定在侧边栏底部 */}
          <div style={{ 
            padding: '12px 16px',
            borderTop: '1px solid #f0f0f0',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center'
          }}>
            <UserMenu />
          </div>
        </div>
      </Sider>

      <Layout style={{ 
        marginLeft: collapsed ? 80 : 250,
        transition: 'margin-left 0.2s'
      }}>
        {/* 顶部工具栏 */}
        <Header style={{ 
          background: '#fff', 
          padding: '0 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* 左侧：折叠按钮 + 标题 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: '16px' }}
            />
            <Title level={4} style={{ margin: 0 }}>
              生物计算平台
            </Title>
          </div>

          {/* 中间：功能按钮 */}
          <div style={{ display: 'flex', justifyContent: 'center', flex: '0 0 auto' }}>
            <Space size="middle">
              <BatchFastaConverter />
              <MultipleSequenceAlignment />
              <StructureAnalysisButton />
            </Space>
          </div>

          {/* 右侧：占位平衡 */}
          <div style={{ flex: 1 }} />
        </Header>

        {/* 主内容区域 */}
        <Content style={{ 
          margin: '24px',
          padding: '24px',
          background: '#fff',
          borderRadius: '8px',
          minHeight: 'calc(100vh - 112px)'
        }}>
          {children}
        </Content>

        {/* 底部 */}
        <Footer style={{ textAlign: 'center', background: '#f0f2f5', padding: '16px 24px' }}>
          <div style={{ marginBottom: 4 }}>
            Copyright © www.cusabio.cn 2007-2026 鄂ICP备15011166号-3
          </div>
          <div style={{ marginBottom: 4 }}>
            鄂公网安备 42018502003126号&nbsp;&nbsp;|&nbsp;&nbsp;
            <a href="/privacy" style={{ color: '#1890ff' }}>隐私权政策</a>
          </div>
        </Footer>
      </Layout>
    </Layout>
  );
};

export default SidebarLayout;